/**
 * Engaged session tracking
 * Fires when user has been on page 30+ seconds AND has interacted with calculator
 */

import { pushEvent } from './dataLayer.js';
import { getPlatform, getAttribution } from './attribution.js';

const ENGAGEMENT_THRESHOLD_MS = 30000; // 30 seconds
const STORAGE_KEY = 'azai_engaged_session';
const CHECK_INTERVAL_MS = 1000; // Check every second

let hasInteracted = false;
let engagedEventFired = false;
let startTime = null;
let checkInterval = null;

/**
 * Check if engaged session was already fired
 */
const wasEngagedEventFired = () => {
  try {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    return sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

/**
 * Mark engaged event as fired
 */
const markEngagedEventFired = () => {
  try {
    engagedEventFired = true;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    }
  } catch (error) {
    // Silent fail
  }
};

/**
 * Check if engagement criteria are met and fire event
 */
const checkEngagement = () => {
  try {
    // Already fired
    if (engagedEventFired) {
      stopEngagementTracking();
      return;
    }
    
    // Check time on page
    const timeOnPage = Date.now() - startTime;
    const hasEnoughTime = timeOnPage >= ENGAGEMENT_THRESHOLD_MS;
    
    // Both conditions must be met
    if (hasEnoughTime && hasInteracted) {
      markEngagedEventFired();
      stopEngagementTracking();
      
      const attribution = getAttribution();
      
      pushEvent('engaged_session', {
        time_on_page_seconds: Math.round(timeOnPage / 1000),
        platform: getPlatform(),
        gclid: attribution.gclid || null,
        utm_source: attribution.utm_source || null,
        utm_medium: attribution.utm_medium || null,
        utm_campaign: attribution.utm_campaign || null,
      });
    }
  } catch (error) {
    // Silent fail
  }
};

/**
 * Stop engagement tracking interval
 */
const stopEngagementTracking = () => {
  if (checkInterval) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
};

/**
 * Mark that user has interacted with calculator
 * Call this when any calculator input changes
 */
export const markCalculatorInteraction = () => {
  try {
    if (hasInteracted) {
      return; // Already marked
    }
    
    hasInteracted = true;
    
    // Check immediately in case time threshold already passed
    checkEngagement();
  } catch (error) {
    // Silent fail
  }
};

/**
 * Initialize engagement tracking
 * @returns {Function} - Cleanup function
 */
export const initEngagementTracking = () => {
  try {
    if (typeof window === 'undefined') {
      return () => {};
    }
    
    // Check if already fired this session
    if (wasEngagedEventFired()) {
      engagedEventFired = true;
      return () => {};
    }
    
    // Start tracking
    startTime = Date.now();
    
    // Set up periodic check
    checkInterval = setInterval(checkEngagement, CHECK_INTERVAL_MS);
    
    return stopEngagementTracking;
  } catch (error) {
    return () => {};
  }
};

/**
 * Reset engagement tracking (useful for testing)
 */
export const resetEngagementTracking = () => {
  try {
    hasInteracted = false;
    engagedEventFired = false;
    startTime = null;
    stopEngagementTracking();
    
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    // Silent fail
  }
};

/**
 * Get current engagement state (useful for debugging)
 */
export const getEngagementState = () => ({
  hasInteracted,
  engagedEventFired,
  timeOnPage: startTime ? Math.round((Date.now() - startTime) / 1000) : 0,
});

export default {
  initEngagementTracking,
  markCalculatorInteraction,
  resetEngagementTracking,
  getEngagementState,
};
